/**
 * Recruitment Invitation Service
 *
 * Manages candidate invitation emails, token generation, and tracking.
 * Follows the same pattern as pre-approval consumer access.
 *
 * MVP: Uses localStorage for invitation records
 * Future: Will migrate to Supabase recruitment_invitations table
 */

import { supabase } from '@/utils/supabase';
import type {
  InvitationRecord,
  SendInvitationData,
  InvitationSendResult,
  InvitationValidationResult,
  InvitationStatus
} from '@/types/recruitment-invitation';
import { EmailService } from './emailService';
import { communicationService } from './enhancedDatabaseService';
import { recruiterNotificationService } from './recruiterNotificationService';
import { resolveTalentCcEmails } from './talentEmailRouting';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('RecruitmentInvitationService', 'recruitment');

const STORAGE_KEY = 'lendwise-recruitment-invitations';
const INVITATION_URL_TEMPLATE = import.meta.env.VITE_RECRUITMENT_PORTAL_URL || 'https://lendwisemtg.com/recruit-login';

export type RecruitOnboardingEmailType = 'partial_onboarding' | 'full_onboarding' | 'nmls_clearance';

export interface RecruitOnboardingSendResult {
  success: boolean;
  deliveryId?: string;
  emailType?: RecruitOnboardingEmailType;
  status?: string;
  error?: string;
}

export class RecruitmentInvitationService {
  /**
   * Generate a cryptographically secure random token
   */
  private static generateSecureToken(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 64; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Generate unique invitation ID
   */
  private static generateInvitationId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Get all invitations from localStorage
   */
  private static getStoredInvitations(): InvitationRecord[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      log.error('Failed to load invitations from localStorage', {}, error as Error);
      return [];
    }
  }

  /**
   * Save invitations to localStorage
   */
  private static saveInvitations(invitations: InvitationRecord[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(invitations));
      log.info('Invitations saved to localStorage', { count: invitations.length });
    } catch (error) {
      log.error('Failed to save invitations to localStorage', {}, error as Error);
    }
  }

  /**
   * Create invitation record (not sent yet)
   */
  static createInvitation(data: SendInvitationData, userId: string, userName: string): InvitationRecord {
    const token = this.generateSecureToken();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + (data.expirationDays * 24 * 60 * 60 * 1000));

    const invitation: InvitationRecord = {
      id: this.generateInvitationId(),
      candidateId: '', // Will be set when sending from drawer
      candidateEmail: data.candidateEmail,
      candidateName: data.candidateName,
      candidateFirstName: data.candidateFirstName,
      candidateLastName: data.candidateLastName,
      token,
      message: data.message,
      inviteUrl: INVITATION_URL_TEMPLATE,
      sentAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      status: 'sent',
      sentBy: userId,
      sentByName: userName,
      ccEmails: data.ccEmails
    };

    return invitation;
  }

  /**
   * Save invitation record to storage
   */
  static saveInvitation(invitation: InvitationRecord): void {
    const invitations = this.getStoredInvitations();
    invitations.push(invitation);
    this.saveInvitations(invitations);

    log.info('Invitation saved', {
      invitationId: invitation.id,
      candidateEmail: invitation.candidateEmail
    });
  }

  /**
   * Get invitations for a specific candidate
   */
  static getInvitationsForCandidate(candidateId: string): InvitationRecord[] {
    const invitations = this.getStoredInvitations();
    return invitations
      .filter(inv => inv.candidateId === candidateId)
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
  }

  /**
   * Get invitation by token
   */
  static getInvitationByToken(token: string): InvitationRecord | null {
    const invitations = this.getStoredInvitations();
    return invitations.find(inv => inv.token === token) || null;
  }

  /**
   * Update invitation status
   */
  static updateInvitationStatus(invitationId: string, status: InvitationStatus): void {
    const invitations = this.getStoredInvitations();
    const invitation = invitations.find(inv => inv.id === invitationId);

    if (invitation) {
      invitation.status = status;
      this.saveInvitations(invitations);

      log.info('Invitation status updated', {
        invitationId,
        newStatus: status
      });
    }
  }

  /**
   * Validate invitation token
   */
  static validateInvitation(token: string): InvitationValidationResult {
    const invitation = this.getInvitationByToken(token);

    if (!invitation) {
      return {
        valid: false,
        error: 'Invalid invitation link'
      };
    }

    // Check expiration
    if (new Date() > new Date(invitation.expiresAt)) {
      this.updateInvitationStatus(invitation.id, 'expired');
      return {
        valid: false,
        error: 'This invitation has expired'
      };
    }

    // Check if revoked
    if (invitation.status === 'revoked') {
      return {
        valid: false,
        error: 'This invitation has been revoked'
      };
    }

    // Update status if first view
    if (invitation.status === 'sent') {
      this.updateInvitationStatus(invitation.id, 'viewed');
    }

    return {
      valid: true,
      invitation
    };
  }

  /**
   * Send invitation email via Gmail API
   */
  static async sendInvitation(
    candidateId: string,
    data: SendInvitationData
  ): Promise<InvitationSendResult> {
    const timer = log.startTimer('send_invitation');

    try {
      // Onboarding emails have a separate server-owned workflow. Refuse here
      // before creating a token, pending communication, localStorage record or
      // last_invited_* side effect.
      if (data.emailType === 'onboarding') {
        return {
          success: false,
          error: 'Onboarding email must use the server-owned delivery workflow'
        };
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return {
          success: false,
          error: 'User not authenticated'
        };
      }

      const userName = user.user_metadata?.full_name ||
                       `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() ||
                       'Your Recruiter';

      // Create invitation record
      const invitation = this.createInvitation(data, user.id, userName);
      invitation.candidateId = candidateId;

      // Log communication as pending
      const communication = await communicationService.logCommunication({
        user_id: user.id,
        type: 'email',
        direction: 'outbound',
        subject: `Invitation to Join Lendwise - ${data.candidateFirstName} ${data.candidateLastName}`,
        content: data.message,
        email_address: data.candidateEmail,
        status: 'pending',
        metadata: {
          invitation_id: invitation.id,
          candidate_id: candidateId,
          invite_url: invitation.inviteUrl,
          channel: 'recruiting'
        }
      });

      invitation.communicationId = communication.id;

      // Fetch recruit data early (need recruiter_id for CC)
      const { data: recruitDetails } = await supabase
        .from('recruits')
        .select('recruiter_id')
        .eq('id', candidateId)
        .single();

      const talentCcEmails = await resolveTalentCcEmails({
        toEmails: [data.candidateEmail],
        ownerUserIds: [user.id, recruitDetails?.recruiter_id],
        extraEmails: [data.ccEmails]
      });
      const talentCcHeader = talentCcEmails.length > 0 ? talentCcEmails.join(', ') : undefined;

      const sendResult = await EmailService.sendRecruitingInvitation(
        data.candidateEmail,
        data.candidateFirstName,
        data.message,
        invitation.inviteUrl,
        userName,
        talentCcHeader,
        communication.id
      );
      // sendRecruitingInvitation returns { success:false } instead of throwing;
      // a failed Gmail send must not mark sent / save / stamp / notify.
      if (!sendResult.success) {
        throw new Error(sendResult.error || 'Failed to send recruiting invitation email');
      }

      // Update communication status to sent
      await communicationService.updateCommunicationStatus(
        communication.id,
        'sent',
        new Date()
      );

      // Save invitation record
      this.saveInvitation(invitation);

      // Stamp the authoritative invite sender on the recruit before notifying.
      // The DB trigger enforces this can only ever be the current user; the
      // owner-routing resolver reads recruits.last_invited_by (not the comm log).
      let stampWarning: string | undefined;
      const { error: stampError } = await supabase
        .from('recruits')
        .update({ last_invited_by: user.id })
        .eq('id', candidateId);
      if (stampError) {
        // Without the authoritative sender, the owner-routing resolver would omit
        // the invite sender from this invitation-sent email AND every later
        // milestone email. Rather than notify without the sender, skip the owner
        // notification and surface the failure so it can be corrected.
        stampWarning = 'Invitation email sent, but recording the invite sender failed; recruiter notifications were skipped.';
        log.error('Failed to stamp recruit last_invited_by — skipping owner notification', {
          candidateId,
          error: stampError.message
        });
      }

      // Notify recruiter (non-blocking) — ONLY when the invite sender was recorded,
      // otherwise the owner-routing resolver would notify without the invite sender.
      if (!stampError) {
        recruiterNotificationService.notifyRecruiterInvitationSent(
          {
            id: candidateId,
            firstName: data.candidateFirstName,
            lastName: data.candidateLastName,
            email: data.candidateEmail,
            recruiterId: recruitDetails?.recruiter_id ?? null
          },
          invitation.inviteUrl,
          invitation.expiresAt
        ).catch(error => {
          log.warn('Failed to send recruiter notification (non-blocking)', {
            candidateId,
            error: error instanceof Error ? error.message : String(error)
          });
        });
      }

      log.endTimer(timer, {
        invitationId: invitation.id,
        candidateEmail: data.candidateEmail,
        success: true
      });

      return {
        success: true,
        invitationId: invitation.id,
        communicationId: communication.id,
        warning: stampWarning
      };

    } catch (error) {
      log.error('Failed to send invitation', {
        candidateEmail: data.candidateEmail
      }, error as Error);

      log.endTimer(timer, { success: false });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send invitation email'
      };
    }
  }

  /**
   * Send or retry one server-owned onboarding delivery. No recipient, copy,
   * credentials, copy text or idempotency key crosses the browser boundary.
   */
  static async sendOnboardingEmail(input:
    | { recruitId: string; emailType: RecruitOnboardingEmailType; resend?: boolean }
    | { deliveryId: string }
  ): Promise<RecruitOnboardingSendResult> {
    try {
      const { data, error } = await supabase.functions.invoke(
        'send-recruit-onboarding-email',
        { body: input }
      );

      if (error) {
        return {
          success: false,
          error: error.message || 'Onboarding email delivery failed'
        };
      }

      const result = data as {
        success?: boolean;
        deliveryId?: string;
        emailType?: RecruitOnboardingEmailType;
        status?: string;
        error?: string;
      } | null;
      if (!result?.success) {
        return {
          success: false,
          deliveryId: result?.deliveryId,
          emailType: result?.emailType,
          status: result?.status,
          error: result?.error || 'Onboarding email delivery failed'
        };
      }

      return {
        success: true,
        deliveryId: result.deliveryId,
        emailType: result.emailType,
        status: result.status
      };
    } catch (error) {
      log.error('Failed to invoke server-owned onboarding email', {
        operation: 'deliveryId' in input ? 'retry' : input.resend ? 'resend' : 'send'
      }, error as Error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Onboarding email delivery failed'
      };
    }
  }

  /**
   * Revoke an invitation
   */
  static revokeInvitation(invitationId: string): void {
    this.updateInvitationStatus(invitationId, 'revoked');
    log.info('Invitation revoked', { invitationId });
  }

  /**
   * Get default invitation message template
   */
  static getDefaultMessage(candidateFirstName: string, position: string = 'Loan Officer'): string {
    return `Hi ${candidateFirstName},

Thank you for your interest in joining Lendwise Mortgage as a ${position}.

We're excited to move forward with your application. Please click the link below to complete your onboarding process, which includes:

• Completing your professional profile
• Uploading required documents (licenses, certifications)
• Reviewing and signing your employment agreement

We look forward to having you on our team!

Best regards,`;
  }

  /**
   * Get default onboarding message template (for candidates with completed tech setup)
   */
  static getDefaultOnboardingMessage(
    candidateFirstName: string,
    onboardingTasks: Array<{ id: string; title: string; status: string }> = []
  ): string {
    // Filter tasks to get only incomplete ones from tasks 6-7 (Xactus and HR Forms)
    const incompleteTasks = onboardingTasks.filter(
      task => ['6', '7'].includes(task.id) && task.status !== 'completed'
    );

    // Generate next steps based on incomplete tasks
    const nextSteps = incompleteTasks.map(task => {
      if (task.id === '6') {
return '• Request your Xactus credentials for billing verification services';
}
      if (task.id === '7') {
return '• Upload identification documents (two forms of government ID)';
}
      return '';
    }).filter(Boolean).join('\n');

    // Check if all tasks are complete
    const allComplete = incompleteTasks.length === 0;

    return `Hi ${candidateFirstName},

Great news! Your technology setup is complete, and you're ready to continue your onboarding.

Your accounts have been configured:
✓ Gmail account assigned
✓ User account linked to Lendwise platform
✓ Optimal Blue account created
✓ Encompass LOS account set up

${allComplete ?
`🎉 You've completed all major onboarding steps! Please finalize your onboarding checklist and begin your training.` :
`Next steps to complete your onboarding:

${nextSteps}
${incompleteTasks.length === 1 ? '' : '• Finalize your onboarding checklist'}`}

Click the link below to continue${allComplete ? ' with your training' : ' with these final steps'}. You're almost there!

Best regards,`;
  }

  /**
   * Export invitations (for future database migration)
   */
  static exportInvitations(): InvitationRecord[] {
    return this.getStoredInvitations();
  }

  /**
   * Clear all invitations (admin only - for testing)
   */
  static clearAllInvitations(): void {
    localStorage.removeItem(STORAGE_KEY);
    log.warn('All invitations cleared from localStorage');
  }
}
